import {
  Button,
  Input,
  Label,
  ListPage,
  RefreshButton,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { X } from 'lucide-react';
import { useState } from 'react';
import { notificationsApi } from '../api/notifications.api';
import { NotificationList } from '../components/notifications/NotificationList';
import { CHANNEL_OPTIONS } from '../constants/channel-colors';
import { ERROR_CATEGORY_INFO, ERROR_CATEGORY_OPTIONS } from '../constants/error-categories';
import { statusTone } from '../utils/status-tone';
import { buildNotificationsQuery } from './list-query';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  'PENDING',
  'QUEUED',
  'PROCESSING',
  // RFC 0002 §2 Fase 3 (en preparación): 'SENT' = aceptado por el proveedor,
  // separado de 'DELIVERED' = confirmado por webhook.
  'SENT',
  'DELIVERED',
  'FAILED',
  'CANCELLED',
  'OPTED_OUT',
];

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export function NotificationsPage() {
  // Single value each: the endpoint declares `status` and `channel` as enums,
  // not arrays, so a multi-select could only ever send something it rejects
  // (noten-g0x).
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<string | null>(null);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [errorCategoryFilter, setErrorCategoryFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const hasFilters =
    statusFilter !== null ||
    channelFilter !== null ||
    eventTypeFilter !== '' ||
    userIdFilter !== '' ||
    errorCategoryFilter !== null;

  const queryParams = buildNotificationsQuery(
    {
      status: statusFilter,
      channel: channelFilter,
      eventType: eventTypeFilter,
      userId: userIdFilter,
      errorCategory: errorCategoryFilter,
    },
    page,
    PAGE_SIZE,
  );

  const {
    data: notifications,
    loading,
    error,
    meta,
    refetch,
  } = notificationsApi.useList(queryParams);
  const total = (meta as { total?: number } | undefined)?.total ?? notifications?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * Clicking the lit chip clears the filter; clicking another replaces it.
   * Always back to page 1 — narrowing the set while sitting on page 3 used to
   * leave the person staring at an empty table that looked like "no results"
   * (noten-g0x).
   */
  const selectFilter = (
    current: string | null,
    value: string,
    setter: (v: string | null) => void,
  ) => {
    setter(current === value ? null : value);
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setChannelFilter(null);
    setEventTypeFilter('');
    setUserIdFilter('');
    setErrorCategoryFilter(null);
    setPage(1);
  };

  return (
    <ListPage
      title="Notifications"
      description="Track delivery, retries, and failures across all channels."
      actions={
        <div className="flex items-center gap-2">
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X /> Clear filters
            </Button>
          ) : null}
          <RefreshButton onRefresh={refetch} isRefreshing={loading} />
        </div>
      }
    >
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((s) => (
                <FilterPill
                  key={s}
                  active={statusFilter === s}
                  onClick={() => selectFilter(statusFilter, s, setStatusFilter)}
                >
                  <StatusBadge
                    tone={statusTone(s)}
                    dot={false}
                    className="border-0 bg-transparent p-0 text-current"
                  >
                    {s}
                  </StatusBadge>
                </FilterPill>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Channel</Label>
            <div className="flex flex-wrap gap-1.5">
              {CHANNEL_OPTIONS.map((c) => (
                <FilterPill
                  key={c}
                  active={channelFilter === c}
                  onClick={() => selectFilter(channelFilter, c, setChannelFilter)}
                >
                  {c}
                </FilterPill>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Last error
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {ERROR_CATEGORY_OPTIONS.map((c) => (
                <FilterPill
                  key={c}
                  active={errorCategoryFilter === c}
                  onClick={() => selectFilter(errorCategoryFilter, c, setErrorCategoryFilter)}
                >
                  <StatusBadge
                    tone={ERROR_CATEGORY_INFO[c].tone}
                    dot={false}
                    title={ERROR_CATEGORY_INFO[c].description}
                    className="border-0 bg-transparent p-0 text-current"
                  >
                    {ERROR_CATEGORY_INFO[c].label}
                  </StatusBadge>
                </FilterPill>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-event">Event type</Label>
            <Input
              id="filter-event"
              type="text"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              placeholder="e.g. user.welcome"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-user">User ID</Label>
            <Input
              id="filter-user"
              type="text"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              placeholder="Filter by user ID"
            />
          </div>
        </div>
      </div>

      <NotificationList
        notifications={notifications ?? []}
        loading={loading}
        error={error as Error | null | undefined}
        page={page}
        pageCount={pageCount}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </ListPage>
  );
}
